import { useContext, useEffect, useRef, useState } from "react";
import {
  CheckOutlined,
  CloseOutlined,
  DownOutlined,
  MoreOutlined
} from "@ant-design/icons";
import {
  Alert,
  Button,
  Dropdown,
  Form,
  Input,
  Modal,
  Popover,
  Radio,
  Select,
  Tooltip
} from "antd";
import type { MenuProps, SelectProps } from "antd";
import { message } from "utils/ciq-message";
import { useCIQQuery } from "hooks/ciq-gql-hooks";
import {
  MUTATION_DELETE_VIEW_CONFIG,
  MUTATION_SAVE_VIEW_CONFIG,
  MUTATION_UPDATE_VIEW_CONFIG
} from "services/graphQL/mutations";
import {
  isPermissionNotGrantted,
  ProjectContext
} from "context/ProjectProvider";
import { useMutation } from "@apollo/client";
import { QUERY_USER_CONFIG_VIEWS } from "services/graphQL/queries";
import { TruncatedTextWithTooltip } from "components/cell-renders";
import UserGroupIcon from "components/svg-icons/user-group-icon";
import styles from "./ColumnsViews.module.scss";
import { EFeaturePageId, ProjectPermissionEnum } from "../../constants";

const MAX_SAVED_VIEWS = 20;
const MAX_SHARED_VIEWS = 30;
const MAX_SHARED_VIEWS_MESSAGE = `Project limit of ${MAX_SHARED_VIEWS} Shared views reached.`;

export type TColumnType = {
  order: number;
  field: {
    name: string;
    filter: any;
    agFilter: string | null;
    sortOrder: "asc" | "desc" | null;
    range: any;
    width: number | null;
  };
  visibility: boolean;
};

export type TSaveColumnState = {
  columnList: Array<TColumnType>;
  quickSearch: string;
  conditionals_formatting?: Array<any>;
};

type TViewUser = {
  first_name: string;
  last_name: string;
  email: string;
};

type TProjectFeatureUserView = {
  id: string;
  name: string;
  column_config: TSaveColumnState;
  created_by: string;
  updated_by: string;
  is_shared: boolean;
  created_by_user: TViewUser;
  updated_by_user: TViewUser;
};

type TUserConfigViewsQueryData = {
  project_feature_user_view: Array<TProjectFeatureUserView>;
};

type Props = {
  setFilterStateFromData: (columnState: TSaveColumnState) => void;
  getColumnStateFromGrid: () => TSaveColumnState | null;
  afterApplyFilterCallBack?: (...args: any[]) => any;
  featureId: EFeaturePageId;
};

type TViewActionMenuView = {
  id: string;
  name: string;
  is_shared: boolean;
  created_by: string;
};

const isViewOwnedByUser = (
  view: TViewActionMenuView,
  userId: unknown
): boolean => String(view.created_by) === String(userId);

const canUserManageView = (
  view: TViewActionMenuView,
  userId: unknown,
  canShareSavedView: boolean
): boolean =>
  isViewOwnedByUser(view, userId) && (!view.is_shared || canShareSavedView);

const buildShareActionLabel = (
  isShared: boolean,
  disableShareAction: boolean
) => {
  if (disableShareAction) {
    return (
      <Tooltip title={MAX_SHARED_VIEWS_MESSAGE} placement="top">
        <div className="w-full block text-ciq-black-a25 cursor-not-allowed">
          Share
        </div>
      </Tooltip>
    );
  }

  return isShared ? "Stop Sharing" : "Share";
};

const buildShareViewMenuItem = (
  view: TViewActionMenuView,
  disableShareAction: boolean,
  onToggleShareView: (view: TViewActionMenuView, shouldShare: boolean) => void
): NonNullable<MenuProps["items"]>[number] => ({
  key: view.is_shared ? "unshare" : "share",
  label: buildShareActionLabel(view.is_shared, disableShareAction),
  onClick: disableShareAction
    ? undefined
    : () => onToggleShareView(view, !view.is_shared)
});

function ColumnConfigViews(props: Props) {
  const {
    setFilterStateFromData,
    getColumnStateFromGrid,
    afterApplyFilterCallBack,
    featureId
  } = props;
  const [form] = Form.useForm();
  const [open, setOpen] = useState(false);
  const [isCreatingView, setCreateViewUI] = useState(false);
  const [editingViewId, setEditingViewId] = useState<string | null>(null);
  const [editingViewName, setEditingViewName] = useState<string>("");
  const [saveOption, setSaveOption] = useState<"create" | "update">("create");
  const [selectedViewId, setSelectedViewId] = useState<string | null>(null);
  const nameValue = Form.useWatch("name", form);
  const isConfirmDialogOpenRef = useRef(false);

  const hide = () => {
    setOpen(false);
  };

  const { gqlClientForProject, tokenContents, eventLogs, isProjectArchived } =
    useContext(ProjectContext);
  const currentUserId = tokenContents?.claims["x-hasura-user-id"];
  const canShareSavedView = !isPermissionNotGrantted(
    ProjectPermissionEnum.CanShareSavedView,
    tokenContents?.role ?? ""
  );

  const canViewSharedViews = !isPermissionNotGrantted(
    ProjectPermissionEnum.CanViewSharedSavedViews,
    tokenContents?.role ?? ""
  );

  const { data: viewsData, refetch: refetchViews } =
    useCIQQuery<TUserConfigViewsQueryData>(QUERY_USER_CONFIG_VIEWS, {
      client: gqlClientForProject,
      skip: !gqlClientForProject,
      variables: { feature_id: featureId }
    });

  const [saveViewColumnsConfig] = useMutation(MUTATION_SAVE_VIEW_CONFIG, {
    client: gqlClientForProject
  });

  const [updateViewColumnsConfig] = useMutation(MUTATION_UPDATE_VIEW_CONFIG, {
    client: gqlClientForProject
  });

  const [deleteViewColumnsConfig] = useMutation(MUTATION_DELETE_VIEW_CONFIG, {
    client: gqlClientForProject
  });

  useEffect(() => {
    if (
      eventLogs.some(
        (event) =>
          event.data_source === "project_feature_user_view" ||
          event.data_source === "project_feature_user_view_by_pk"
      )
    ) {
      refetchViews();
    }
  }, [eventLogs, refetchViews]);

  const onViewApply = async (view: TSaveColumnState) => {
    setFilterStateFromData(view);
    hide();
    setTimeout(() => {
      if (afterApplyFilterCallBack) afterApplyFilterCallBack();
    }, 500);
  };

  const onCreateNewView = async (name: string) => {
    const currentColumnState = getColumnStateFromGrid() as any;
    if (currentColumnState) {
      const res = await saveViewColumnsConfig({
        variables: {
          name,
          feature_id: featureId,
          column_config: currentColumnState
        }
      });
      if (res.data) {
        message.success("Successfully added a view");
        refetchViews();
      } else if (res.errors) {
        message.error(res.errors[0].message);
      }
    }
  };

  const onUpdateExistingView = async (viewId: string) => {
    const currentColumnState = getColumnStateFromGrid() as any;
    const viewToUpdate = viewsData?.project_feature_user_view.find(
      (v) => v.id === viewId
    );

    if (!viewToUpdate) {
      message.error("View not found.");
      return false;
    }

    if (!currentColumnState) return false;

    if (viewToUpdate.is_shared && !canShareSavedView) {
      message.error("You don't have permission to update a shared view.");
      return false;
    }

    if (viewToUpdate.is_shared) {
      const confirmed = await confirmSharedViewUpdate(viewToUpdate.name);
      if (!confirmed) return false;
    }

    const res = await updateViewColumnsConfig({
      variables: {
        id: viewId,
        data: {
          name: viewToUpdate.name,
          column_config: currentColumnState
        }
      }
    });

    if (res.data) {
      message.success(`Successfully updated '${viewToUpdate.name}'.`);
      refetchViews();
    } else if (res.errors) {
      message.error(res.errors[0].message);
    }

    return true;
  };

  const onDeleteView = (view: {
    id: string;
    name: string;
    is_shared?: boolean;
  }) => {
    if (view.is_shared && !canShareSavedView) {
      message.error("You don't have permission to delete a shared view.");
      return;
    }
    isConfirmDialogOpenRef.current = true;
    Modal.confirm({
      zIndex: 9999,
      title: (
        <div className="max-w-full flex items-center gap-1">
          <span className="text-base text-ciq-black-a88 shrink-0">
            Delete View -
          </span>
          <div className="min-w-0 grow">
            <TruncatedTextWithTooltip
              text={view.name}
              className="text-base text-ciq-black-a88"
              tooltipZIndex={11000}
            />
          </div>
        </div>
      ),
      icon: null,
      content: (
        <div className="flex items-start gap-3">
          <span className="text-base">
            {view.is_shared
              ? "This view is being shared with others. Are you sure you want to delete this view?"
              : "Are you sure you want to delete this view?"}
          </span>
        </div>
      ),
      okText: "Delete",
      cancelText: "Cancel",
      okButtonProps: {
        style: {
          backgroundColor: "#4448C1",
          borderColor: "#4448C1"
        }
      },
      cancelButtonProps: {
        style: {
          borderColor: "#D9D9D9"
        }
      },
      onOk: async () => {
        // hide();
        const res = await deleteViewColumnsConfig({
          variables: { id: view.id }
        });
        if (res.data) {
          message.success(`Successfully removed ${view.name}.`);
          refetchViews();
        } else if (res.errors) {
          message.error(res.errors[0].message);
        }
      },
      afterClose: () => {
        isConfirmDialogOpenRef.current = false;
      }
    });
  };

  const confirmSharedViewUpdate = async (viewName: string) => {
    isConfirmDialogOpenRef.current = true;
    return new Promise<boolean>((resolve) => {
      Modal.confirm({
        zIndex: 9999,
        title: (
          <div className="max-w-full flex items-center gap-1">
            <span className="text-base text-ciq-black-a88 shrink-0">
              Update View -
            </span>
            <div className="min-w-0 grow">
              <TruncatedTextWithTooltip
                text={viewName}
                className="text-base text-ciq-black-a88"
                tooltipZIndex={11000}
              />
            </div>
          </div>
        ),
        icon: null,
        content: (
          <div className="text-base text-ciq-black-a88">
            The changes will be reflected for everyone who has access to this
            view.
            <br />
            Do you want to continue?
          </div>
        ),
        okText: "Update",
        cancelText: "Cancel",
        okButtonProps: {
          style: {
            backgroundColor: "#4448C1",
            borderColor: "#4448C1"
          }
        },
        cancelButtonProps: {
          style: {
            borderColor: "#D9D9D9"
          }
        },
        onOk: () => resolve(true),
        onCancel: () => resolve(false),
        afterClose: () => {
          hide();
          isConfirmDialogOpenRef.current = false;
        }
      });
    });
  };

  const onToggleShareView = (
    view: { id: string; name: string; is_shared: boolean },
    shouldShare: boolean
  ) => {
    if (!canShareSavedView) return;
    const sharedViewsCount =
      viewsData?.project_feature_user_view.filter((v) => v.is_shared).length ??
      0;
    if (shouldShare && sharedViewsCount >= MAX_SHARED_VIEWS) {
      message.warning(MAX_SHARED_VIEWS_MESSAGE);
      return;
    }
    isConfirmDialogOpenRef.current = true;
    Modal.confirm({
      zIndex: 9999,
      title: (
        <div className="max-w-full flex items-center gap-1">
          <span className="text-base text-ciq-black-a88 shrink-0">
            {shouldShare ? "Share View -" : "Stop Sharing -"}
          </span>
          <div className="min-w-0 grow">
            <TruncatedTextWithTooltip
              text={view.name}
              className="text-base text-ciq-black-a88"
              tooltipZIndex={11000}
            />
          </div>
        </div>
      ),
      icon: null,
      content: (
        <div>
          {shouldShare ? (
            <div className="text-base text-ciq-black-a88">
              <div>
                Sharing this view will make it available to all project users
                with the following project roles. Do you want to continue?
              </div>
              <ul className="list-disc pl-5 mt-2">
                <li>Project Admin/Manager/Engineer</li>
                <li>Foreman/Superintendent</li>
              </ul>
            </div>
          ) : (
            <div className="text-base text-ciq-black-a88">
              Are you sure you no longer want to share this view with others?
            </div>
          )}
        </div>
      ),
      okText: shouldShare ? "Share" : "Stop Sharing",
      cancelText: "Cancel",
      okButtonProps: {
        style: {
          backgroundColor: "#4448C1",
          borderColor: "#4448C1"
        }
      },
      onOk: async () => {
        // hide();
        const res = await updateViewColumnsConfig({
          variables: {
            id: view.id,
            data: {
              is_shared: shouldShare
            }
          }
        });
        if (res.data) {
          message.success(
            `Successfully ${shouldShare ? "shared" : "stopped sharing"} ${view.name}.`
          );
          refetchViews();
        } else if (res.errors) {
          message.error(res.errors[0].message);
        }
      },
      afterClose: () => {
        isConfirmDialogOpenRef.current = false;
      }
    });
  };

  const onStartEdit = (view: { id: string; name: string }) => {
    const viewMeta = viewsData?.project_feature_user_view.find(
      (v) => v.id === view.id
    );
    if (viewMeta && String(viewMeta.created_by) !== String(currentUserId)) {
      message.error("You can only rename views created by you.");
      return;
    }
    if (viewMeta?.is_shared && !canShareSavedView) {
      message.error("You don't have permission to rename a shared view.");
      return;
    }
    setEditingViewId(view.id);
    setEditingViewName(view.name);
  };

  const onCancelEdit = () => {
    setEditingViewId(null);
    setEditingViewName("");
  };

  const handleOpenChange = (newOpen: boolean) => {
    if (!newOpen && isConfirmDialogOpenRef.current) {
      return;
    }
    if (editingViewId) {
      onCancelEdit();
    }
    setCreateViewUI(false);
    setSaveOption("create");
    setSelectedViewId(null);
    form.resetFields();
    setOpen(newOpen);
  };

  const onUpdateViewName = async (viewId: string) => {
    const trimmedName = editingViewName.trim();

    if (!trimmedName) {
      return;
    }

    // Check for duplicate names
    const isDuplicateName =
      viewsData &&
      viewsData.project_feature_user_view.some(
        (v) => v.name.toLowerCase() === trimmedName.toLowerCase()
      );

    if (isDuplicateName || !trimmedName) {
      refetchViews();
      setEditingViewId(null);
      setEditingViewName("");
      return;
    }

    // Find the current view to get its column config
    const currentView = viewsData?.project_feature_user_view.find(
      (v) => v.id === viewId
    );

    if (!currentView) {
      message.error("View not found.");
      return;
    }

    if (String(currentView.created_by) !== String(currentUserId)) {
      message.error("You can only rename views created by you.");
      setEditingViewId(null);
      setEditingViewName("");
      return;
    }

    if (currentView.is_shared && !canShareSavedView) {
      message.error("You don't have permission to rename a shared view.");
      setEditingViewId(null);
      setEditingViewName("");
      return;
    }

    if (currentView.is_shared) {
      const confirmed = await confirmSharedViewUpdate(currentView.name);
      if (!confirmed) return;
    }

    const res = await updateViewColumnsConfig({
      variables: {
        id: viewId,
        data: {
          name: trimmedName,
          column_config: currentView?.column_config
        }
      }
    });

    if (res.data) {
      message.success("Successfully updated view name.");
      refetchViews();
      setEditingViewId(null);
      setEditingViewName("");
    } else if (res.errors) {
      message.error(res.errors[0].message);
    }
  };

  const getViewActionMenuItems = (
    view: TViewActionMenuView
  ): MenuProps["items"] => {
    const items: MenuProps["items"] = [];
    if (isProjectArchived) return items;
    const sharedViewsCount =
      viewsData?.project_feature_user_view.filter((v) => v.is_shared).length ??
      0;
    const isShareLimitReached = sharedViewsCount >= MAX_SHARED_VIEWS;
    const disableShareAction = !view.is_shared && isShareLimitReached;

    if (canUserManageView(view, currentUserId, canShareSavedView)) {
      items.push({
        key: "rename",
        label: <span data-testid="button-edit">Rename</span>,
        onClick: () => onStartEdit(view)
      });
    }

    if (canShareSavedView && isViewOwnedByUser(view, currentUserId)) {
      items.splice(
        1,
        0,
        buildShareViewMenuItem(view, disableShareAction, onToggleShareView)
      );
    }

    if (canUserManageView(view, currentUserId, canShareSavedView)) {
      items.push({
        key: "delete",
        label: (
          <span className="text-red-500" data-testid="button-delete">
            Delete
          </span>
        ),
        onClick: () => onDeleteView(view)
      });
    }

    return items;
  };

  const allViews = viewsData?.project_feature_user_view || [];
  const savedViews = canViewSharedViews
    ? allViews.filter((v) => !(v.is_shared && v.created_by !== currentUserId))
    : allViews.filter((v) => !v.is_shared);
  const sharedWithMeViews = canViewSharedViews
    ? allViews.filter((v) => v.is_shared && v.created_by !== currentUserId)
    : [];
  const sortedSavedViews = [...savedViews].sort((a, b) =>
    a.name.localeCompare(b.name, undefined, { sensitivity: "base" })
  );
  const sortedSharedWithMeViews = [...sharedWithMeViews].sort((a, b) =>
    a.name.localeCompare(b.name, undefined, { sensitivity: "base" })
  );

  const updatableViews = allViews.filter(
    (v) => String(v.created_by) === String(currentUserId)
  );
  const currentUserViewsCount = updatableViews.length;

  const updateExistingViewSelectOptions: SelectProps["options"] =
    updatableViews.map((v) => ({ label: v.name, value: v.id }));

  const getUserInitials = (user?: TViewUser) => {
    const first = (user?.first_name || "").trim();
    const last = (user?.last_name || "").trim();
    if (first || last) {
      return `${first.charAt(0)}${last.charAt(0)}`.toUpperCase() || "U";
    }

    const emailPrefix = (user?.email || "").split("@")[0]?.trim() || "";
    const emailChunks = emailPrefix.split(/[.\-_ ]+/).filter(Boolean);
    if (emailChunks.length > 1) {
      return `${emailChunks[0].charAt(0)}${emailChunks[1].charAt(0)}`.toUpperCase();
    }
    if (emailChunks.length === 1) {
      return emailChunks[0].slice(0, 2).toUpperCase();
    }
    return "U";
  };

  const getSharedByTooltip = (user?: TViewUser) => {
    const fullName = [user?.first_name, user?.last_name]
      .filter(Boolean)
      .join(" ")
      .trim();
    const displayName = fullName || user?.email || "Unknown user";
    return `Shared by ${displayName}`;
  };

  const renderViewRow = (
    v: {
      id: string;
      name: string;
      column_config: TSaveColumnState;
      created_by: string;
      is_shared: boolean;
      created_by_user: TViewUser;
    },
    shouldShowSharedIcon: boolean,
    isSharedWithMeRow = false
  ) => (
    <div
      key={v.id}
      className="w-full flex items-center px-4 py-3 justify-between bg-white hover:bg-gray-50 border-b"
    >
      {editingViewId === v.id ? (
        <>
          <Input
            value={editingViewName}
            onChange={(e) => setEditingViewName(e.target.value)}
            onPressEnter={() => onUpdateViewName(v.id)}
            className="flex-1 mr-3"
            // eslint-disable-next-line jsx-a11y/no-autofocus
            autoFocus
            data-testid="edit-view-name-input"
          />
          <div className="flex items-center space-x-3 flex-shrink-0">
            <Tooltip title="Save">
              <CheckOutlined
                className="cursor-pointer text-ciq-primary-900 hover:text-ciq-primary-900 text-base"
                onClick={() => onUpdateViewName(v.id)}
                data-testid="button-save-edit"
                style={{
                  backgroundColor: "var(--ciq-gray-300)",
                  borderRadius: 6,
                  border: "1px solid var(--ciq-black-a6)",
                  padding: 2,
                  width: 24,
                  height: 24
                }}
              />
            </Tooltip>
            <Tooltip title="Cancel">
              <CloseOutlined
                className="cursor-pointer text-ciq-primary-900 hover:text-ciq-primary-900 text-base"
                onClick={onCancelEdit}
                data-testid="button-cancel-edit"
                style={{
                  backgroundColor: "var(--ciq-gray-300)",
                  borderRadius: 6,
                  border: "1px solid var(--ciq-black-a6)",
                  padding: 2,
                  width: 24,
                  height: 24
                }}
              />
            </Tooltip>
          </div>
        </>
      ) : (
        <>
          <div
            className="w-full min-w-0 cursor-pointer text-sm text-ciq-black-a88 mr-3 flex items-center gap-3"
            role="button"
            tabIndex={0}
            onClick={() => {
              onViewApply(v.column_config);
            }}
            onKeyDown={(e) => {
              if (e.key === "Enter" || e.key === " ") {
                e.preventDefault();
                onViewApply(v.column_config);
              }
            }}
          >
            <TruncatedTextWithTooltip
              text={v.name}
              className="text-sm text-ciq-black-a88"
            />
            {canViewSharedViews && shouldShowSharedIcon && (
              <Tooltip title="View is shared with everyone">
                <span className="inline-flex items-center">
                  <UserGroupIcon />
                </span>
              </Tooltip>
            )}
          </div>
          {isSharedWithMeRow && (
            <Tooltip title={getSharedByTooltip(v.created_by_user)}>
              <div className="w-[24px] h-[24px] rounded-full bg-ciq-gray-400 text-white text-[10px] leading-none font-medium tracking-[0.4px] flex items-center justify-center flex-shrink-0 select-none">
                {getUserInitials(v.created_by_user)}
              </div>
            </Tooltip>
          )}
          {(() => {
            const actionMenuItems = getViewActionMenuItems(v);
            if (!actionMenuItems || actionMenuItems.length === 0) return null;
            return (
              <div className="flex items-center space-x-3 flex-shrink-0">
                <Dropdown
                  trigger={["click"]}
                  menu={{ items: actionMenuItems }}
                  placement="bottomRight"
                  align={{ points: ["tl", "tr"], offset: [8, 0] }}
                  getPopupContainer={() => document.body}
                  autoAdjustOverflow
                >
                  <MoreOutlined
                    className="cursor-pointer text-ciq-gray-700 hover:text-ciq-gray-900 text-base"
                    onClick={(e) => e.stopPropagation()}
                    data-testid="button-view-actions"
                  />
                </Dropdown>
              </div>
            );
          })()}
        </>
      )}
    </div>
  );

  const listViewContent = (
    <div
      className={styles.boxStyle}
      style={{ borderRadius: 4, border: "none", overflow: "hidden" }}
    >
      {sortedSavedViews.length > 0 && (
        <div className="px-4 pt-3 pb-0.5 font-semibold text-xs text-ciq-black-a45">
          Saved Views
        </div>
      )}
      <div className="max-h-64 overflow-y-auto">
        {sortedSavedViews.map((v) =>
          renderViewRow(
            v,
            Boolean(v.is_shared) &&
              String(v.created_by) === String(currentUserId)
          )
        )}
        {canViewSharedViews && sortedSharedWithMeViews.length > 0 && (
          <>
            <hr className="my-0 mt-1 mx-1 border-t-0 border-b border-dotted border-ciq-gray-300" />
            <div className="px-4 pt-3 pb-0.5 mt-1 font-semibold text-xs text-ciq-black-a45">
              Shared with me
            </div>
          </>
        )}
        {canViewSharedViews &&
          sortedSharedWithMeViews.map((v) => renderViewRow(v, false, true))}
      </div>
      <div className="px-4 py-3 bg-gray-50">
        <div className="flex items-center justify-between">
          <div className="text-ciq-black/65">
            <div className="font-semibold text-xs ">Save Current View</div>
            <div className="text-xs font-normal">
              Create new or update existing.
            </div>
          </div>
          <Button
            type="primary"
            onClick={() => {
              // Auto-select the appropriate save option based on current state
              if (currentUserViewsCount >= MAX_SAVED_VIEWS) {
                setSaveOption("update");
              } else if (currentUserViewsCount === 0) {
                setSaveOption("create");
              } else {
                setSaveOption("create");
              }
              setCreateViewUI(true);
            }}
            disabled={!!isProjectArchived}
            data-testid="index-button"
          >
            Save
          </Button>
        </div>
      </div>
    </div>
  );

  const saveViewModal = (
    <div className={styles.boxStyle} style={{ border: "none" }}>
      <Form
        form={form}
        layout="vertical"
        className="bg-white p-3"
        onFinish={async (values) => {
          if (saveOption === "create") {
            const name = (values.name || "").trim();
            await onCreateNewView(name);
          } else if (selectedViewId) {
            const shouldClose = await onUpdateExistingView(selectedViewId);
            if (!shouldClose) return;
          } else {
            message.error("Please select a view to update.");
            return;
          }
          setCreateViewUI(false);
          setSaveOption("create");
          setSelectedViewId(null);
          form.resetFields();
        }}
      >
        <div className={`mb-6 ${styles.customRadioGroup}`}>
          <div className="font-semibold text-sm mb-3">
            How would you like to save the view?
          </div>
          <Radio.Group
            onChange={(e) => {
              setSaveOption(e.target.value);
              setSelectedViewId(null);
            }}
            value={saveOption}
          >
            <div className="space-y-3">
              <div>
                <Radio
                  value="create"
                  disabled={currentUserViewsCount >= MAX_SAVED_VIEWS}
                >
                  <span className="text-sm">Create a new view</span>
                </Radio>
                {currentUserViewsCount >= MAX_SAVED_VIEWS && (
                  <div className="ml-6 p-0">
                    <Alert
                      // antd v6 renamed alert internals (ant-alert-message -> ant-alert-title); wrap message in a span with a stable data-testid so automation isn't coupled to antd class names
                      title={
                        <span data-testid="max-views-reached-alert-message">{`Maximum of ${MAX_SAVED_VIEWS} views reached.`}</span>
                      }
                      type="info"
                      showIcon
                      className="rounded-xl"
                      data-testid="max-views-reached-alert"
                    />
                  </div>
                )}
                {saveOption === "create" &&
                  currentUserViewsCount < MAX_SAVED_VIEWS && (
                    <Form.Item
                      className="ml-6"
                      label={
                        <span className="font-semibold text-xs">Name</span>
                      }
                      name="name"
                      required
                      rules={[
                        {
                          required: true,
                          whitespace: true,
                          message: "Please enter view name."
                        },
                        {
                          validator(rule, value) {
                            if (value) {
                              const name = value.trim().toLowerCase();
                              if (
                                updatableViews.some(
                                  (v) => v.name.toLowerCase() === name
                                )
                              )
                                return Promise.reject(
                                  new Error("View name already exists.")
                                );
                            }
                            return Promise.resolve();
                          }
                        }
                      ]}
                    >
                      <Input
                        placeholder="Enter name"
                        className="w-full"
                        data-testid="index-view-name-input"
                      />
                    </Form.Item>
                  )}
              </div>
              <div className="w-full">
                <Radio value="update" disabled={currentUserViewsCount === 0}>
                  <span className="text-sm">Update an existing view</span>
                </Radio>
                {saveOption === "update" && (
                  <Form.Item
                    className="ml-6"
                    label={
                      <span className="font-semibold text-xs">Select View</span>
                    }
                    required
                  >
                    <Select
                      placeholder="Select view"
                      value={selectedViewId}
                      onChange={(value) => setSelectedViewId(value)}
                      data-testid="index-view-select"
                      options={updateExistingViewSelectOptions}
                    />
                  </Form.Item>
                )}
              </div>
            </div>
          </Radio.Group>
        </div>

        <div className="flex justify-end space-x-4">
          <Button
            onClick={() => {
              setCreateViewUI(false);
              setSaveOption("create");
              setSelectedViewId(null);
              form.resetFields();
            }}
            data-testid="index-button-cancel"
          >
            Cancel
          </Button>
          <Button
            htmlType="submit"
            type="primary"
            data-testid="index-button-save"
            disabled={
              saveOption === "create"
                ? !nameValue || !nameValue.trim()
                : !selectedViewId
            }
          >
            Save
          </Button>
        </div>
      </Form>
    </div>
  );

  return (
    <Popover
      content={isCreatingView ? saveViewModal : listViewContent}
      trigger="click"
      open={open}
      onOpenChange={handleOpenChange}
      classNames={{ root: styles.popoverNoPadding }}
      placement="bottomRight"
      // antd v6 renamed `showArrow` to `arrow`; the old prop is ignored.
      arrow={false}
      data-testid="column-config-popover"
    >
      <Button data-testid="index-button-views">
        Views <DownOutlined className="text-xs" />
      </Button>
    </Popover>
  );
}
export default ColumnConfigViews;
